import { GrecoCompany } from "types/types";
import {
  ImportedInvoice,
  ImportedInvoiceEntry,
  ImportedInvoiceMatchState,
} from "types/importedInvoices";

// taxonomy Country id of Latvia (the PaymentPlanEntry view lists Latvian companies by this id)
export const LATVIA_COUNTRY_CODE_ID = 949;

// The page belongs to GrECo Latvia (QTG invoices): only users allowed to work for a
// Latvian GrECo company get the toolbar button and the route. Admins hold every company.
export const hasLatvianCompany = (
  grecoCompanies: GrecoCompany[] | null | undefined
): boolean =>
  (grecoCompanies ?? []).some(
    (gc) => gc.companyCountryCodeId === LATVIA_COUNTRY_CODE_ID
  );

// Columns of the Bank statements page that show imported (QTG) invoices; hidden, like the
// page itself, from users without a Latvian GrECo company.
export const QTG_INVOICE_COLUMN_IDS = [
  "pendingImportedInvoiceNumber", // bank statement entries: invoice paid but not clearable yet
  "importedInvoiceNumber", // clearance dialog: invoice named in the payment reference
];

export const withQtgInvoiceColumns = <T extends { columnId: string }>(
  columns: T[],
  grecoCompanies: GrecoCompany[] | null | undefined
): T[] =>
  hasLatvianCompany(grecoCompanies)
    ? columns
    : columns.filter((c) => !QTG_INVOICE_COLUMN_IDS.includes(c.columnId));

export const isEntryMatched = (entry: ImportedInvoiceEntry): boolean =>
  entry.paymentPlanEntryId !== null && entry.paymentPlanEntryId !== undefined;

export const isEntryPaid = (entry: ImportedInvoiceEntry): boolean =>
  entry.paymentId !== null && entry.paymentId !== undefined;

// the premium of the invoice entry must equal the premium of its installment; a manual match may differ
export const hasPremiumDifference = (entry: ImportedInvoiceEntry): boolean =>
  isEntryMatched(entry) &&
  entry.premiumAmountPaymentPlanEntry !== null &&
  entry.premiumAmountPaymentPlanEntry !== undefined &&
  Math.abs(entry.premiumAmount - entry.premiumAmountPaymentPlanEntry) > 0.005;

// counters of the master row recomputed from its entries (after a manual match / unmatch)
export const countImportedInvoiceEntries = (
  entries: ImportedInvoiceEntry[]
): {
  entryCount: number;
  matchedEntryCount: number;
  paidEntryCount: number;
  unmatchedEntryCount: number;
} => {
  const entryCount = entries.length;
  const matchedEntryCount = entries.filter(isEntryMatched).length;
  const paidEntryCount = entries.filter(isEntryPaid).length;
  return {
    entryCount,
    matchedEntryCount,
    paidEntryCount,
    unmatchedEntryCount: entryCount - matchedEntryCount,
  };
};

// same rule as the backend (ImportedInvoiceMatcher.FillInMatchState)
export const deriveMatchState = (
  invoice: Pick<ImportedInvoice, "pendingBankStatementEntryId">,
  entries: ImportedInvoiceEntry[]
): ImportedInvoiceMatchState => {
  const { entryCount, matchedEntryCount, paidEntryCount } =
    countImportedInvoiceEntries(entries);
  if (entryCount > 0 && paidEntryCount === entryCount) return "paid";
  if (invoice.pendingBankStatementEntryId !== null && invoice.pendingBankStatementEntryId !== undefined) return "pending";
  if (entryCount > 0 && matchedEntryCount === entryCount) return "matched";
  if (matchedEntryCount > 0) return "partiallyMatched";
  return "unmatched";
};

// states that still need the user's attention
export const needsAttention = (state: ImportedInvoiceMatchState): boolean =>
  state === "unmatched" || state === "partiallyMatched" || state === "pending";
